/**
 * Plush delivery pincode.
 *
 * Lets the shopper set the "Delivering to <pincode>" value. Their choice is kept
 * in localStorage and shown on every page/visit, so it behaves like the pincode
 * pickers on Indian marketplaces. Falls back to the store's default pincode until
 * the shopper sets one. No backend needed; serviceability/ETA would be a further
 * step (a courier API).
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'plush_delivery_pincode';

  function getSaved() {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return null;
    }
  }

  function save(pincode) {
    try {
      localStorage.setItem(STORAGE_KEY, pincode);
    } catch (e) {
      /* private mode / storage disabled — the value just won't persist */
    }
  }

  function isValid(value) {
    // Indian pincodes: 6 digits, not starting with 0.
    return /^[1-9][0-9]{5}$/.test(value);
  }

  class PlushDelivery extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('.header__delivery-trigger');
      this.pop = this.querySelector('.header__delivery-pop');
      this.input = this.querySelector('[data-delivery-input]');
      this.saveBtn = this.querySelector('[data-delivery-save]');
      this.error = this.querySelector('[data-delivery-error]');
      // Every page shares the same pincode label via this attribute.
      this.labels = document.querySelectorAll('[data-delivery-pincode]');

      // Show the saved pincode (if any) in place of the default.
      var saved = getSaved();
      if (saved && isValid(saved)) this.setLabel(saved);

      if (!this.trigger || !this.pop) return;

      this.trigger.addEventListener('click', this.toggle.bind(this));
      this.saveBtn.addEventListener('click', this.commit.bind(this));
      this.input.addEventListener('keydown', this.onKey.bind(this));
      this.input.addEventListener('input', this.onInput.bind(this));
      document.addEventListener('click', this.onOutside.bind(this));
      document.addEventListener('keydown', this.onEscape.bind(this));
    }

    setLabel(pincode) {
      this.labels.forEach(function (el) {
        el.textContent = pincode;
      });
    }

    currentPincode() {
      var el = this.querySelector('[data-delivery-pincode]');
      return (el && el.textContent.trim()) || this.getAttribute('data-default-pincode') || '';
    }

    open() {
      this.pop.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
      this.input.value = this.currentPincode();
      this.hideError();
      this.input.focus();
      this.input.select();
    }

    close() {
      this.pop.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
    }

    toggle(event) {
      event.preventDefault();
      if (this.pop.hidden) this.open();
      else this.close();
    }

    onInput() {
      // Digits only.
      var cleaned = this.input.value.replace(/[^\d]/g, '');
      if (cleaned !== this.input.value) this.input.value = cleaned;
      this.hideError();
    }

    onKey(event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.commit();
      }
    }

    commit() {
      var value = this.input.value.trim();
      if (!isValid(value)) {
        this.showError();
        return;
      }
      save(value);
      this.setLabel(value);
      this.close();
    }

    showError() {
      if (this.error) this.error.hidden = false;
      this.input.focus();
    }

    hideError() {
      if (this.error) this.error.hidden = true;
    }

    onOutside(event) {
      if (!this.pop.hidden && !this.contains(event.target)) this.close();
    }

    onEscape(event) {
      if (event.key === 'Escape' && !this.pop.hidden) {
        this.close();
        this.trigger.focus();
      }
    }
  }

  if (!customElements.get('plush-delivery')) {
    customElements.define('plush-delivery', PlushDelivery);
  }
})();
